import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apiService from '../services/apiService';
import { CLOUDFLARE_CONFIG, API_CONFIG } from '../config/api';

const UserDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [roleTypes, setRoleTypes] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);

  useEffect(() => {
    if (id) {
      fetchUserDetails();
      fetchRoleTypes();
      fetchAgencies();
    }
  }, [id]);

  const fetchUserDetails = async () => {
    try {
      setLoading(true);
      
      const data = await apiService.get(`/users/${id}`);
      setUserData(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching user details:', err);
      setError('Failed to load user details');
    } finally {
      setLoading(false);
    }
  };

  const fetchRoleTypes = async () => {
    try {
      const data = await apiService.get('/user-role-types');
      setRoleTypes(data);
    } catch (err) {
      console.error('Error fetching role types:', err);
    }
  };

  const fetchAgencies = async () => {
    try {
      const data = await apiService.get('/agencies');
      setAgencies(data);
    } catch (err) {
      console.error('Error fetching agencies:', err);
    }
  };

  const handleInputChange = (field, value) => {
    setUserData((prev) => (prev ? { ...prev, [field]: value } : prev));
  };

  const handleRoleTypeChange = (roleTypeValue) => {
    setUserData((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        userRoleType: roleTypeValue,
        ...(roleTypeValue !== 'Agent' ? { agencyName: '' } : {}),
      };
    });
  };

  const handleAgencyChange = (agencyNameValue) => {
    setUserData((prev) => (prev ? { ...prev, agencyName: agencyNameValue } : prev));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      // Find the selected role type ID
      const selectedRoleType = roleTypes.find(role => 
        (role.title || role.name) === userData.userRoleType
      );
      
      // Find the selected agency ID
      const selectedAgency = agencies.find(agency => 
        agency.agencyName === userData.agencyName
      );

      const requestBody = {
        editedBy: 3,
        userRoleTypeId: selectedRoleType ? selectedRoleType.id : 0,
        agencyId: selectedAgency ? selectedAgency.id : 0,
        sellerCompanyId: 1,
        firstName: userData.firstName || "",
        lastName: userData.lastName || "",
        emailAddress: userData.emailAddress || "",
        cellNumber: userData.cellNumber || "",
        companyName: userData.companyName || "",
        notes: "",
        isActive: userData.isActive !== undefined ? userData.isActive : true
      };

      await apiService.put(`/users/?id=${id}`, requestBody);
      
      // Optionally refresh the user data
      await fetchUserDetails();
      
      alert('User updated successfully!');
      
    } catch (err) {
      console.error('Error updating user:', err);
      alert('Failed to update user. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpdate = async () => {
    if (!selectedFile) {
      alert('Please select a file first');
      return;
    }

    try {
      setIsUploadingImage(true);

      // Step 1: Upload to Cloudflare - try direct first, fall back to proxies if CORS blocks
      const cloudflareFormData = new FormData();
      cloudflareFormData.append("file", selectedFile);

      let cloudflareResponse = null;

      // Try direct upload first (no proxy)
      try {
        const directResponse = await fetch(CLOUDFLARE_CONFIG.IMAGES_ENDPOINT, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${CLOUDFLARE_CONFIG.API_TOKEN}`,
          },
          body: cloudflareFormData,
        });

        if (directResponse.ok) {
          cloudflareResponse = await directResponse.json();
        }
      } catch (error) {
        console.log('Direct upload failed (likely CORS), trying proxies...', error);
      }

      // Only use proxies if direct upload failed
      if (!cloudflareResponse) {
        for (const proxy of API_CONFIG.CORS_PROXIES) {
          try {
            const response = await fetch(
              proxy + CLOUDFLARE_CONFIG.IMAGES_ENDPOINT,
              {
                method: "POST",
                headers: {
                  Authorization: `Bearer ${CLOUDFLARE_CONFIG.API_TOKEN}`,
                },
                body: cloudflareFormData,
              }
            );

            if (response.ok) {
              cloudflareResponse = await response.json();
              break;
            }
          } catch (error) {
            console.log(`Proxy ${proxy} failed:`, error);
            continue;
          }
        }
      }

      if (!cloudflareResponse || !cloudflareResponse.success || !cloudflareResponse.result.variants) {
        throw new Error("Failed to upload image to Cloudflare");
      }

      // Step 2: Get the URL from Cloudflare response
      const imageUrl = cloudflareResponse.result.variants[0];

      // Step 3: Post to users/update-thumb-image endpoint
      await apiService.put('/users/update-thumb-image', {
        userId: parseInt(id),
        thumbImage: imageUrl
      });

      // Refresh user data to show the new image
      await fetchUserDetails();

      // Clear the selected file
      setSelectedFile(null);
      const fileInput = document.getElementById('profileImage');
      if (fileInput) {
        fileInput.value = '';
      }

      alert('Profile image updated successfully!');

    } catch (error) {
      console.error('Error updating profile image:', error);
      alert('Failed to update profile image. Please try again.');
    } finally {
      setIsUploadingImage(false);
    }
  };

  if (loading) {
    return (
      <div className="manage-properties">
        <div className="page-header">
          <h1>User Details</h1>
        </div>
        <div className="loading-state">
          <p>Loading user details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="manage-properties">
        <div className="page-header">
          <h1>User Details</h1>
        </div>
        <div className="error-state">
          <p>{error}</p>
          <button onClick={fetchUserDetails} className="retry-button">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!userData) {
    return (
      <div className="manage-properties">
        <div className="page-header">
          <h1>User Details</h1>
        </div>
        <div className="error-state">
          <p>User not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="manage-properties">
      <div className="page-header">
        <h1>User Details</h1>
      </div>

      <div className="content-container">
        <div className="form-sections-row">
          <div className="form-section half-width">
            <h3>Profile Image</h3>
            <div className="profile-image-row">
              <div className="image-display">
                {userData.thumbImage ? (
                  <img 
                    src={userData.thumbImage} 
                    alt="User thumbnail" 
                    className="user-thumbnail"
                  />
                ) : (
                  <div className="profile-image-placeholder">
                    <svg className="placeholder-icon" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                    </svg>
                    <span>No Profile Image</span>
                  </div>
                )}
              </div>
              
              <div className="image-upload-controls">
                <div className="form-row">
                  <label htmlFor="profileImage">Upload New:</label>
                  <input
                    type="file"
                    id="profileImage"
                    className="form-input file-input-half"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        setSelectedFile(file);
                        // Show preview of selected image
                        const reader = new FileReader();
                        reader.onload = (event) => {
                          // Preview functionality can be added here if needed
                          console.log('Preview URL:', event.target.result);
                        };
                        reader.readAsDataURL(file);
                      }
                    }}
                    disabled={isUploadingImage}
                  />
                </div>
                
                <button 
                  type="button" 
                  className="btn-primary"
                  onClick={handleImageUpdate}
                  disabled={isUploadingImage || !selectedFile}
                >
                  {isUploadingImage ? 'Uploading...' : (userData.thumbImage ? 'Update' : 'Upload')}
                </button>
              </div>
            </div>
          </div>

          <div className="form-section half-width">
            <h3>Update Password</h3>

            <div className="form-row">
              <label htmlFor="newPassword">New Password:</label>
              <input
                type="password"
                id="newPassword"
                className="form-input"
                placeholder="Enter new password"
              />
            </div>
            <div className="form-actions">
              <button 
                type="button" 
                className="btn-primary"
                onClick={async () => {
                  const newPasswordInput = document.getElementById('newPassword');
                  const newPassword = newPasswordInput.value.trim();
                  
                  if (!newPassword) {
                    alert('Please enter a new password');
                    return;
                  }
                  
                  try {
                    await apiService.post('/auth/admin-reset-password', {
                      adminUserId: 4,
                      userId: parseInt(id),
                      newPassword: newPassword
                    });
                    
                    alert('Password updated successfully!');
                    newPasswordInput.value = ''; // Clear the input
                  } catch (error) {
                    console.error('Error updating password:', error);
                    alert('Failed to update password. Please try again.');
                  }
                }}
              >
                Update Password
              </button>
            </div>


          </div>
        </div>

        <div className="form-section">
          <h3>Personal Information</h3>
          <div className="form-grid">
            <div className="form-row">
              <label htmlFor="firstName">First Name:</label>
              <input
                type="text"
                id="firstName"
                className="form-input"
                value={userData.firstName || ''}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
              />
            </div>
            
            <div className="form-row">
              <label htmlFor="lastName">Last Name:</label>
              <input
                type="text"
                id="lastName"
                className="form-input"
                value={userData.lastName || ''}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
              />
            </div>
            
            <div className="form-row">
              <label htmlFor="emailAddress">Email Address:</label>
              <input
                type="email"
                id="emailAddress"
                className="form-input"
                value={userData.emailAddress || ''}
                onChange={(e) => handleInputChange('emailAddress', e.target.value)}
              />
            </div>
            
            <div className="form-row">
              <label htmlFor="cellNumber">Cell Number:</label>
              <input
                type="text"
                id="cellNumber"
                className="form-input"
                value={userData.cellNumber || ''}
                onChange={(e) => handleInputChange('cellNumber', e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="form-section">
          <h3>User Role & Agency</h3>
          <div className="form-grid">
            <div className="form-row">
              <label>Role Type:</label>
              <select 
                className="form-select"
                value={userData.userRoleType || ''}
                onChange={(e) => handleRoleTypeChange(e.target.value)}
              >
                <option value="">Select a role type</option>
                {roleTypes.map((role) => (
                  <option key={role.id} value={role.title || role.name}>
                    {role.title || role.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="form-row">
              <label>Agency:</label>
              <select 
                className="form-select"
                value={userData.agencyName || ''}
                onChange={(e) => handleAgencyChange(e.target.value)}
                disabled={userData.userRoleType !== 'Agent'}
              >
                <option value="">Select an agency</option>
                {agencies.map((agency) => (
                  <option key={agency.id} value={agency.agencyName}>
                    {agency.agencyName}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-row">
              <label>Active Status:</label>
              <div className="checkbox-group">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={userData.isActive !== undefined ? userData.isActive : true}
                  onChange={(e) => handleInputChange('isActive', e.target.checked)}
                  className="form-checkbox"
                />
                <span>User is active</span>
              </div>
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button 
            type="button" 
            className="btn-primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserDetails;


